import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { io } from "socket.io-client"; 
import { 
  Search, UserPlus, Wallet, Activity, X, PlusCircle, 
  History, Upload, FileCheck, ChevronRight, ArrowLeft, 
  Trash2, CreditCard, Inbox, ChevronLeft, CheckCircle, Calendar, AlertCircle, Loader2, Info, Banknote, Award, WifiOff, RefreshCw
} from "lucide-react";
import { useAuth } from "../../component/context/AuthContext";
import styles from "../../styles/StaffList.module.css";
import toast, { Toaster } from 'react-hot-toast';

const socket = io("https://salon-admin.deyfreshsignature.com", { 
  withCredentials: true,
  transports: ['polling', 'websocket'] 
});

axios.defaults.withCredentials = true;

export default function StaffList() {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState(user?.role?.replace("_", "-") || ""); 
  const [errors, setErrors] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof window !== "undefined" ? navigator.onLine : true);

  // UI States
  const [searchTerm, setSearchTerm] = useState("");
  const [staffFilter, setStaffFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [taskCommission, setTaskCommission] = useState("30");
  const [step, setStep] = useState(1);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); 
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showWorkDetailsModal, setShowWorkDetailsModal] = useState(false);
  const [activeWorkItem, setActiveWorkItem] = useState(null);
  const [globalViewRequests, setGlobalViewRequests] = useState(false);
  const [notify, setNotify] = useState("");
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Form States
  const [taskTitle, setTaskTitle] = useState("");
  const [taskAmount, setTaskAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  
  // Loan Form States
  const [loanAmount, setLoanAmount] = useState("");
  const [loanDeduction, setLoanDeduction] = useState("");
  const [loanFrequency, setLoanFrequency] = useState("daily");
  const [staffLoans, setStaffLoans] = useState([]);

  const [newStaff, setNewStaff] = useState({
    name: "", email: "", phone: "", role: "Barber", 
    birthday: "", address: "", 
    bank_name: "", account_number: "", account_name: "", 
    guarantor_name: "", guarantor_phone: "", guarantor_address: ""
  });

  // FetchData wrapped inside useCallback to manage background automatic reload intervals safely
  const fetchData = useCallback(async () => {
    if (!navigator.onLine) {
      setIsOnline(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const staffRes = await axios.get(`/api/get-staff`);
      setStaffList(staffRes.data);
      
      if (userRole === "super-admin") {
        const reqRes = await axios.get(`/api/admin/all-withdrawals`);
        setWithdrawalRequests(reqRes.data);
      }
    } catch (err) {
      console.error("Connection Error:", err);
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  // LIVE NETWORK MONITOR HOOKS
  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(true);
      toast.success("Network connection restored! Synchronizing records...", { icon: "⚡" });
      fetchData();
    };

    const handleOfflineStatus = () => {
      setIsOnline(false);
      toast.error("Network connection dropped. System offline.", { icon: "📡" });
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOfflineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOfflineStatus);
    };
  }, [fetchData]);

  useEffect(() => {
    if (userRole === "super-admin") {
      socket.emit('join_room', 'super_admins');
    }

    socket.on('new_withdrawal_request', (data) => {
      const normalizedRequest = {
        id: data.requestId,
        staff_id: data.staffId,
        staff_name: data.staffName, 
        amount: data.amount,
        requested_by: data.requestedBy,
        status: 'Pending',
        created_at: new Date().toISOString()
      };
      setWithdrawalRequests(prev => [normalizedRequest, ...prev]);
      setGlobalViewRequests(true); 
      setActiveTab("requests");
    });

    fetchData();

    return () => {
      socket.off('new_withdrawal_request');
    };
  }, [userRole, fetchData]);

  useEffect(() => {
    if (user?.role) setUserRole(user.role.replace("_", "-"));
  }, [user]);

  const fetchStaffLoans = async (staffId) => {
    try {
      const res = await axios.get(`/api/admin/loans/${staffId}`);
      setStaffLoans(res.data || []);
    } catch (err) {
      console.error("Failed to load loan histories");
    }
  };

  useEffect(() => {
    if (selectedStaff) {
      fetchStaffLoans(selectedStaff.id);
    }
  }, [selectedStaff]);

  const handleApplyLoan = async (e) => {
    e.preventDefault();
    if (!loanAmount || !loanDeduction) return toast.error("Please fill in tracking metrics entirely.");
    setIsActionLoading(true);
    try {
      await axios.post("/api/admin/loans/request", {
        staffId: selectedStaff.id,
        amount: Number(loanAmount),
        deductionAmount: Number(loanDeduction),
        frequency: loanFrequency
      });
      toast.success("Loan applied successfully!");
      setShowLoanModal(false);
      setLoanAmount("");
      setLoanDeduction("");
      fetchStaffLoans(selectedStaff.id);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to finalize loan entry request.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleProcessLoanAdmin = async (loanId, targetStatus) => {
    try {
      await axios.post("/api/admin/loans/status", { loanId, status: targetStatus });
      toast.success(`Loan successfully marked as ${targetStatus}`);
      fetchStaffLoans(selectedStaff.id);
      fetchData(); 
      
      if (targetStatus === 'Approved') {
        const approvedLoan = staffLoans.find(l => l.id === loanId);
        if (approvedLoan && selectedStaff) {
          const updatedStaff = {
            ...selectedStaff,
            loan_balance: (Number(selectedStaff.loan_balance) || 0) + Number(approvedLoan.principal_amount)
          };
          setSelectedStaff(updatedStaff);
          setStaffList(prev => prev.map(s => s.id === updatedStaff.id ? updatedStaff : s));
        }
      }
    } catch (err) {
      toast.error("Failed handling workflow event");
    }
  };

  const validateStep2 = () => {
     let temp = {};
     if (!newStaff.bank_name) temp.bank_name = "Bank Name is required";
     if (!newStaff.account_number || !/^\d{10}$/.test(newStaff.account_number)) temp.account_number = "Valid 10-digit Account Number is required";
     if (!newStaff.account_name) temp.account_name = "Account Name is required";
     setErrors(temp);
     return Object.keys(temp).length === 0;
  }

  const validateStep1 = () => {
    let tempErrors = {};
    if (!newStaff.name) tempErrors.name = "Full Name is required";
    if (!newStaff.email || !/\S+@\S+\.\S+/.test(newStaff.email)) tempErrors.email = "Valid Email is required";
    if (!/^\d{11}$/.test(newStaff.phone)) tempErrors.phone = "Enter valid 11-digit phone parameters";
    if (!newStaff.address) tempErrors.address = "Address is required";
    if (!newStaff.birthday) tempErrors.birthday = "Birthday is required";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleAddTask = async () => {
    if (!taskAmount || !taskTitle || !taskCommission) return;
    setIsActionLoading(true);
    const loadingToast = toast.loading("Recording task...");
    try {
      const res = await axios.post(`/api/add-task`, {
        staffId: selectedStaff.id,
        title: taskTitle,
        totalAmount: Number(taskAmount),
        commissionRate: Number(taskCommission)
      });
      
      const { earned, company } = res.data;
      const newTask = { 
          id: Date.now(), 
          title: taskTitle, 
          earned_amount: earned, 
          company_share: company, 
          date: new Date().toISOString() 
      };
      
      const updatedStaff = {
        ...selectedStaff,
        wallet_balance: Number(selectedStaff.wallet_balance) + earned,
        history: [newTask, ...(selectedStaff.history || [])]
      };
      
      setSelectedStaff(updatedStaff);
      setStaffList(prevList => prevList.map(s => s.id === selectedStaff.id ? updatedStaff : s));
      setTaskAmount(""); 
      setTaskTitle("");
      toast.success("Task recorded cleanly!", { id: loadingToast });
    } catch (err) {
      toast.error("Failed running procedure.", { id: loadingToast });
    }  finally {
      setIsActionLoading(false);
    }
  };

  const handleWithdrawInitiation = async () => {
    const amount = Number(withdrawAmount);
    if (!amount || amount > selectedStaff.wallet_balance) return toast.error("Invalid input bounds.");
    setIsActionLoading(true);
    try {
      const res = await axios.post(`/api/withdraw-request`, {
        staffId: selectedStaff.id,
        amount,
        requestedBy: userRole === 'super-admin' ? 'Super Admin' : 'Manager'
      });
      toast.success(res.data.message);
      setShowWithdrawModal(false);
      setWithdrawAmount("");
      fetchData();
    } catch (err) {
      toast.error("Error processing withdrawal configuration.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeclineRequest = async (request) => {
    if (!window.confirm(`Decline layout values?`)) return;
    try {
      await axios.post(`/api/decline-payout`, { requestId: request.id });
      setWithdrawalRequests(prev => prev.map(r => r.id === request.id ? { ...r, status: 'Declined' } : r));
      toast.success("Declined context successfully.");
    } catch (err) {
      toast.error("Failed operation context mapping.");
    }
  };

  const handleApproveRequest = async (request) => {
    setIsActionLoading(true);
    const loadId = toast.loading("Processing transaction execution parameters...");
    try {
      await axios.post(`/api/approve-payout`, { requestId: request.id });
      setWithdrawalRequests(prev => prev.map(r => r.id === request.id ? { ...r, status: 'Approved' } : r));
      
      setStaffList(prev => prev.map(s => {
        if (s.id === request.staff_id) {
          return { ...s, wallet_balance: Number(s.wallet_balance) - Number(request.amount) };
        }
        return s;
      }));

      if (selectedStaff?.id === request.staff_id) {
         const newTransaction = {
            id: Date.now(),
            type: "Withdrawal",
            amount: Number(request.amount),
            date: new Date().toISOString()
         };
         setSelectedStaff(prev => ({
           ...prev,
           wallet_balance: Number(prev.wallet_balance) - Number(request.amount),
           transactions: [newTransaction, ...(prev.transactions || [])]
         }));
      }
      toast.success("Payout Approved", { id: loadId });
    } catch (err) {
      toast.error("Approval sequence halted layout errors", { id: loadId });
    } finally {
      setIsActionLoading(false);
    }
  };

  const computeArtisanTierBadge = (totalGrossVolume) => {
    if (totalGrossVolume >= 500000) return { title: "ELITE MASTER", color: "#cedc07" };
    if (totalGrossVolume >= 200000) return { title: "SENIOR RESIDENT", color: "#caa45f" };
    return { title: "RISING ARTISAN", color: "#888" };
  };

  const calculateStats = (staff) => {
    if (!staff.history) return { workToday: 0, totalWork: 0, earningsToday: 0, totalEarnings: 0, totalWithdrawals: 0, totalGrossInflow: 0 };
    const today = new Date().toLocaleDateString();
    const workToday = staff.history.filter(h => new Date(h.date).toLocaleDateString() === today);
    const earningsToday = workToday.reduce((sum, h) => sum + Number(h.earned_amount), 0);
    const totalEarnings = staff.history.reduce((sum, h) => sum + Number(h.earned_amount), 0);
    
    // Summing only transaction types that correspond to standard withdrawals
    const totalWithdrawals = staff.transactions?.reduce((sum, t) => {
      return t.type === "Withdrawal" ? sum + Number(t.amount) : sum;
    }, 0) || 0;

    const totalGrossInflow = staff.history.reduce((sum, h) => sum + (Number(h.earned_amount) + Number(h.company_share || 0)), 0);

    return { 
      workToday: workToday.length, 
      totalWork: staff.history.length, 
      earningsToday, 
      totalEarnings, 
      totalWithdrawals,
      totalGrossInflow 
    };
  };

  const paginate = (data) => {
    if (!data) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const handleRemoveStaff = async (id, name) => {
    if (!window.confirm(`Are you sure ${name} has Left DeyFresh Signature?`)) return;
    const confirmSecond = window.confirm("This will mark them as 'Left'. Their history will be preserved. Proceed?");
    if (!confirmSecond) return;
    
    try {
      await axios.put(`/api/admin/remove-staff/${id}`, { status: 'Left' });
      setStaffList(prev => prev.map(s => s.id === id ? { ...s, status: 'Left' } : s));
      setSelectedStaff(null); 
      toast.success("Artisan record updated to 'Left'.");
    } catch (err) {
      toast.error("Process execution fault parameters error.");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure there was a mistake? This will deduct the earned amount from this worker's wallet balance permanently.")) return;
    setIsActionLoading(true);
    const loadToast = toast.loading("Reverting balances metrics...");
    try {
      await axios.delete(`/api/admin/delete-task/${taskId}`, {
        data: { staffId: selectedStaff.id }
      });
      const filteredHistory = (selectedStaff.history || []).filter(t => t.id !== taskId);
      const updatedStaff = {
        ...selectedStaff,
        wallet_balance: Number(selectedStaff.wallet_balance) - (Number(activeWorkItem?.earned_amount) || 0),
        history: filteredHistory
      };
      setSelectedStaff(updatedStaff);
      setStaffList(prev => prev.map(s => s.id === updatedStaff.id ? updatedStaff : s));
      setShowWorkDetailsModal(false);
      setActiveWorkItem(null);
      toast.success("Task removed and balances adjusted successfully!", { id: loadToast });
    } catch (err) {
      toast.error("Failed to delete task instance.", { id: loadToast });
    } finally {
      setIsActionLoading(false);
    }
  };

  const numericalAmount = Number(taskAmount) || 0;
  const numericalRate = Number(taskCommission) || 0;
  const liveStaffEarns = numericalAmount * (numericalRate / 100);
  const liveCompanyShare = numericalAmount * (1 - numericalRate / 100);

  // OFFLINE TERMINAL CONTAINER INTERCEPTOR VIEW
  if (!isOnline) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', backgroundColor: '#000', color: '#fff', textAlign: 'center', padding: '20px' }}>
        <WifiOff size={64} color="#ff4d4d" style={{ marginBottom: '20px' }} />
        <h2 style={{ color: '#ff4d4d', letterSpacing: '1px', fontWeight: '900', fontSize: '24px', margin: '0 0 10px 0' }}>TERMINAL OFFLINE</h2>
        <p style={{ color: '#888', maxWidth: '400px', fontSize: '14px', margin: '0 0 30px 0', lineHeight: '1.5' }}>
          DeyFresh Signature connection dropped. Check your shop router or local network parameters.
        </p>
        <button 
          onClick={fetchData}
          style={{ background: 'transparent', color: '#caa45f', border: '1px solid #caa45f', padding: '14px 28px', fontSize: '12px', fontWeight: '900', letterSpacing: '2px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.3s ease' }}
          onMouseEnter={(e) => { e.target.style.background = '#caa45f'; e.target.style.color = '#000'; }}
          onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#caa45f'; }}
        >
          <RefreshCw size={14} /> RETRY CONNECTION
        </button>
      </div>
    );
  }

  if (loading) return <div className={styles.loading}>Connecting to DeyFresh...</div>;

  return (
    <div className={styles["staff-portal-wrapper"]}>
      <Toaster position="top-right" toastOptions={{ style: { background: '#111', color: '#fff', border: '1px solid #caa45f' } }} />
      <section className={styles["staff-hero"]}>
        <div>
          <span className={styles["eyebrow"]}>TEAM & PERFORMANCE</span>
          <h1>Staff Management</h1>
          <p>Manage your salon team, track services completed, commissions, payouts and staff activity from one place.</p>
        </div>
        <div className={styles["hero-actions"]}>
          {userRole === "super-admin" && withdrawalRequests.filter(r => r.status === 'Pending').length > 0 && (
            <button className={styles["request-btn"]} onClick={() => { const req = withdrawalRequests.find(r => r.status === "Pending"); const staff = req && staffList.find(s => String(s.id) === String(req.staff_id)); if (staff) { setSelectedStaff(staff); setActiveTab("withdrawals"); setCurrentPage(1); } else { setGlobalViewRequests(true); setCurrentPage(1); } }}>
              <AlertCircle size={17} /> {withdrawalRequests.filter(r => r.status === 'Pending').length} Payout Requests
            </button>
          )}
          <button className={styles["gold-action-btn"]} onClick={() => { setShowAddModal(true); setStep(1); }}><UserPlus size={18} /> <span>Add Staff Member</span></button>
        </div>
      </section>

      <section className={styles["team-overview"]}>
        <div className={styles["overview-card"]}><div className={styles["overview-icon"]}><Activity size={20}/></div><div><span>Total Team</span><strong>{staffList.length}</strong><small>Registered staff</small></div></div>
        <div className={styles["overview-card"]}><div className={styles["overview-icon"]}><CheckCircle size={20}/></div><div><span>Active Staff</span><strong>{staffList.filter(s => s.status === 'Active').length}</strong><small>Available team members</small></div></div>
        <div className={styles["overview-card"]}><div className={styles["overview-icon"]}><Wallet size={20}/></div><div><span>Staff Wallets</span><strong>₦{staffList.reduce((sum,s) => sum + Number(s.wallet_balance || 0), 0).toLocaleString()}</strong><small>Current payable balance</small></div></div>
        <div className={styles["overview-card"]}><div className={styles["overview-icon"]}><Award size={20}/></div><div><span>Services Today</span><strong>{staffList.reduce((sum,s) => sum + calculateStats(s).workToday, 0)}</strong><small>Completed by the team</small></div></div>
      </section>

      <section className={styles["team-toolbar"]}>
        <div className={styles["premium-search-box"]}>
          <Search size={18} /><input type="text" value={searchTerm} placeholder="Search staff by name or role..." onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className={styles["staff-filters"]}>
          {[["all","All Staff"],["active","Active"],["offline","Offline"],["left","Former Staff"]].map(([key,label]) => (
            <button key={key} className={staffFilter === key ? styles["filter-active"] : ""} onClick={() => setStaffFilter(key)}>{label}</button>
          ))}
        </div>
      </section>

      <div className={styles["section-heading"]}>
        <div><h2>Team Directory</h2><p>Select a staff member to view performance, work history, commission, loans and payouts.</p></div>
        <span>{staffList.filter(s => {
          const q = searchTerm.toLowerCase();
          const matchesSearch = (s.name || '').toLowerCase().includes(q) || (s.role || '').toLowerCase().includes(q);
          const matchesFilter = staffFilter === 'all' || (staffFilter === 'active' && s.status === 'Active') || (staffFilter === 'offline' && s.status !== 'Active' && s.status !== 'Left') || (staffFilter === 'left' && s.status === 'Left');
          return matchesSearch && matchesFilter;
        }).length} people</span>
      </div>

      <div className={styles["staff-grid-layout"]}>
        {staffList.filter(s => {
          const q = searchTerm.toLowerCase();
          const matchesSearch = (s.name || '').toLowerCase().includes(q) || (s.role || '').toLowerCase().includes(q);
          const matchesFilter = staffFilter === 'all' || (staffFilter === 'active' && s.status === 'Active') || (staffFilter === 'offline' && s.status !== 'Active' && s.status !== 'Left') || (staffFilter === 'left' && s.status === 'Left');
          return matchesSearch && matchesFilter;
        }).map((staff) => {
          const individualStats = calculateStats(staff);
          const tier = computeArtisanTierBadge(individualStats.totalGrossInflow);

          return (
            <button type="button" key={staff.id} className={`${styles["staff-card-compact"]} ${staff.status === 'Left' ? styles.staffLeft : ""}`} onClick={() => {setSelectedStaff(staff); setActiveTab("overview"); setCurrentPage(1);}}>
              <div className={styles["staff-card-top"]}>
                <div className={styles["avatar-wrap"]}>
                  <img src={staff.image_url || "https://i.pravatar.cc/150"} alt={staff.name || "Staff member"} className={styles["staff-img-small"]} />
                  {staff.status !== 'Left' && <div className={`${styles["status-dot"]} ${staff.status === 'Active' ? styles.online : styles.offline}`}></div>}
                </div>
                <span className={styles["staff-status-label"]}>{staff.status || 'Offline'}</span>
              </div>
              <div className={styles["staff-meta"]}>
                <h3>{staff.name}{staff.status === 'Left' && <span className={styles["left-indicator"]}> · Former staff</span>}</h3>
                <span className={styles["role-tag"]}>{staff.role}</span>
              </div>
              <div className={styles["staff-joined-line"]}><Calendar size={13}/><span>Joined</span><strong>{staff.created_at ? new Date(staff.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : 'Not available'}</strong></div>
              <div className={styles["staff-mini-stats"]}>
                <div><span>Today</span><strong>{individualStats.workToday}</strong><small>services</small></div>
                <div><span>All time</span><strong>{individualStats.totalWork}</strong><small>services</small></div>
              </div>
              <div className={styles["staff-card-footer"]}>
                <div><span>Wallet balance</span><strong>₦{Number(staff.wallet_balance || 0).toLocaleString()}</strong></div>
                <ChevronRight size={19}/>
              </div>
            </button>
          );
        })}
      </div>

      {/* GLOBAL CENTRALIZED WITHDRAWAL REQUESTS MANAGER HUB OVERLAY */}
      {globalViewRequests && userRole === "super-admin" && (
        <div className={styles["modal-fixed-overlay"]}>
          <div className={`${styles["portfolio-modal-content"]} ${styles["large-modal"]}`} style={{ maxWidth: '700px', minHeight: '400px' }}>
            <div className={styles["terminal-header"]} style={{ padding: '25px 35px 10px 35px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className={styles["terminal-title-wrap"]}>
                <AlertCircle size={20} color="#ff4d4d" />
                <h3 style={{ fontSize: '14px', letterSpacing: '1px', fontWeight: '900', color: '#fff', margin: 0 }}>STAFF PAYOUT REQUESTS</h3>
              </div>
              <button className={styles["close-circle-btn"]} onClick={() => setGlobalViewRequests(false)}><X size={20} /></button>
            </div>
            <div style={{ padding: '0 35px 35px 35px' }}>
              <div className={styles["history-feed"]} style={{ marginTop: '20px' }}>
                {withdrawalRequests.length > 0 ? (
                  <>
                    {paginate(withdrawalRequests).map(req => (
                      <div key={req.id} className={styles["history-item"]} style={{ background: '#090909', border: '1px solid #111', padding: '15px', marginBottom: '10px' }}>
                        <div className={styles["h-info"]}>
                          <strong style={{ color: '#caa45f', fontSize: '14px' }}>{req.staff_name}</strong>
                          <small style={{ color: '#fff', display: 'block', margin: '4px 0', fontSize: '13px', fontWeight: '700' }}>
                            REQUESTED PAYOUT: ₦{Number(req.amount).toLocaleString()}
                          </small>
                          <span style={{ fontSize: '10px', color: '#666' }}>Channel: Via system {req.requested_by} • {new Date(req.created_at).toLocaleString()}</span>
                        </div>
                        <div className={styles["h-actions"]} style={{ display: 'flex', gap: '10px' }}>
                          {req.status === 'Pending' ? (
                            <>
                              <button className={styles["approve-btn"]} style={{ background: '#00ff9c', color: '#000', fontWeight: '900', border: 'none', padding: '8px 16px', cursor: 'pointer' }} onClick={() => handleApproveRequest(req)}>APPROVE</button>
                              <button className={styles["decline-btn"]} style={{ background: '#ff4d4d', color: '#fff', fontWeight: '900', border: 'none', padding: '8px 16px', cursor: 'pointer' }} onClick={() => handleDeclineRequest(req)}>DECLINE</button>
                            </>
                          ) : (
                            <span className={`${styles["status-pill"]} ${styles[req.status.toLowerCase()]}`} style={{ fontWeight: '900', letterSpacing: '1px', fontSize: '11px' }}>{req.status.toUpperCase()}</span>
                          )}
                        </div>
                      </div>
                    ))}
                    <Pagination totalItems={withdrawalRequests.length} itemsPerPage={itemsPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                  </>
                ) : (
                  <div className={styles["empty-state"]} style={{ padding: '60px 0' }}><Inbox size={40} /><p>No staff payout requests yet.</p></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* INDIVIDUAL STAFF PROFILE DETAIL WORKFLOW LAYER */}
      {selectedStaff && (() => {
        const stats = calculateStats(selectedStaff);
        const tier = computeArtisanTierBadge(stats.totalGrossInflow);

        const joinedDate = selectedStaff.created_at 
          ? new Date(selectedStaff.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })
          : "N/A";

        return (
          <div className={styles["modal-fixed-overlay"]}>
            <div className={`${styles["portfolio-modal-content"]} ${styles["large-modal"]}`}>
              <div className={styles["portfolio-grid"]}>
                <div className={styles["portfolio-sidebar"]}>
                  <div style={{ position: 'relative', width: '100%', textAlign: 'center' }}>
                    <img src={selectedStaff.image_url || "https://i.pravatar.cc/150"} alt="" className={styles["p-img-large"]} />
                   
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-4px', marginBottom: '15px' }}>
                      <span style={{ background: '#000', border: `1px solid ${tier.color}`, color: tier.color, fontSize: '9px', fontWeight: '900', letterSpacing: '1px', padding: '4px 10px', borderRadius: '2px', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.6)' }}>
                        <Award size={11} /> {tier.title}
                      </span>
                    </div>
                  </div>
                  
                  <h2 className={styles["p-name"]} style={{color:"#cedc07", fontSize:18, marginTop: '5px'}}>{selectedStaff.name}</h2>
                  <span className={styles["p-role"]}>{selectedStaff.role}</span>
                  
                  <div className={styles["join-date-badge"]} style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', color: '#777', fontSize: '11px', fontWeight: '800', marginTop: '4px' }}>
                    <Calendar size={12} color="#caa45f" /> <span>Member since {joinedDate}</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20, margin: '25px 0' }}>
                    <div className={styles["dual-balance-row-container"]}>
                      <div className={styles["balance-split-column"]}>
                        <span className={styles["balance-top-label"]}>WALLET BALANCE</span>
                        <h3 className={styles["balance-value-green"]}>₦{Number(selectedStaff.wallet_balance).toLocaleString()}</h3>
                      </div>
                      <div className={styles["balance-vertical-divider"]}></div>
                      <div className={styles["balance-split-column"]}>
                        <span className={styles["balance-top-label"]} style={{ color: '#ff4d4d' }}>LOAN BALANCE</span>
                        <h3 className={styles["balance-value-red"]}>₦{Number(selectedStaff.loan_balance || 0).toLocaleString()}</h3>
                      </div>
                    </div>

                    <div style={{ display:'flex', flexDirection:'column', gap: 8 }}>
                      <button className={styles["premium-withdraw-btn"]} onClick={() => setShowWithdrawModal(true)}><CreditCard size={14} /> {userRole === "super-admin" ? "Deduct Funds" : "Request Payout"}</button>
                      <button className={styles["premium-withdraw-btn"]} style={{ borderColor: '#caa45f', color: '#caa45f' }} onClick={() => setShowLoanModal(true)}><Banknote size={14} /> Loan Management</button>
                    </div>
                  </div>

                  {/* RESTORED: EXPANDED 5-TILE PERFORMANCE PANEL WITH TOTAL WITHDRAWALS */}
                  <div className={styles["stats-grid"]} style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className={styles["stat-card"]}><Activity size={16} className={styles["stat-icon"]}/><span>Work Today</span><h2 style={{color:'#fff', fontSize: '18px'}}>{stats.workToday}</h2></div>
                    <div className={styles["stat-card"]}><History size={16} className={styles["stat-icon"]}/><span>Total Work</span><h2 style={{color:'#fff', fontSize: '18px'}}>{stats.totalWork}</h2></div>
                    <div className={styles["stat-card"]}><Wallet size={16} className={styles["stat-icon"]}/><span>Earn Today</span><h2 style={{color:'#fff', fontSize: '18px'}}>₦{stats.earningsToday.toLocaleString()}</h2></div>
                    <div className={styles["stat-card"]}><Wallet size={16} className={styles["stat-icon"]}/><span>Total Net Earn</span><h2 style={{color:'#fff', fontSize: '18px'}}>₦{stats.totalEarnings.toLocaleString()}</h2></div>
                    <div className={styles["stat-card"]} style={{ gridColumn: 'span 2' }}><CreditCard size={16} className={styles["stat-icon"]} style={{color: '#ff4d4d'}}/><span style={{color: '#ff4d4d'}}>Total Withdrawal</span><h2 style={{color:'#ff4d4d', fontSize: '18px'}}>₦{stats.totalWithdrawals.toLocaleString()}</h2></div>
                  </div>

                  <div className={styles["sidebar-footer-actions"]} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '10px', marginTop: '20px' }}>
                    <button className={styles["close-profile-btn"]} style={{ flex: 1 }} onClick={() => {setSelectedStaff(null); setActiveTab("overview");}}><ArrowLeft size={14} /> Close Profile</button>
                    
                    {userRole === "super-admin" && (
                      <button 
                        className={styles["remove-staff-btn"]} 
                        style={{ background: 'rgba(255, 77, 77, 0.1)', color: '#ff4d4d', border: '1px solid #ff4d4d', padding: '12px', cursor: 'pointer', fontWeight: '900', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => {
                          if (selectedStaff.status === 'Left') {
                            toast.success("This staff member is already marked as 'Left'.");
                            return;
                          }
                          handleRemoveStaff(selectedStaff.id, selectedStaff.name);
                        }}
                      >
                        <Trash2 size={14} /> Remove Staff
                      </button>
                    )}
                  </div>
                </div>

                <div className={styles["portfolio-main"]}>
                  <div className={styles["profile-modal-header"]}>
                    <div>
                      <span className={styles["profile-kicker"]}>STAFF PROFILE</span>
                      <h2>{selectedStaff.name}</h2>
                      <p>View staff details, performance, work records, wallet activity and withdrawal requests.</p>
                    </div>
                    <button className={styles["profile-close"]} onClick={() => {setSelectedStaff(null); setActiveTab("overview");}}><X size={20}/></button>
                  </div>
                  <div className={styles["portfolio-tabs"]}>
                    <button className={activeTab === "overview" ? styles.active : ""} onClick={() => {setActiveTab("overview"); setCurrentPage(1);}}>Overview</button>
                    <button className={activeTab === "task" ? styles.active : ""} onClick={() => {setActiveTab("task"); setCurrentPage(1);}}>New Task</button>
                    <button className={activeTab === "work" ? styles.active : ""} onClick={() => {setActiveTab("work"); setCurrentPage(1);}}>Work History</button>
                    <button className={activeTab === "transactions" ? styles.active : ""} onClick={() => {setActiveTab("transactions"); setCurrentPage(1);}}>Payouts</button>
                    <button className={activeTab === "withdrawals" ? styles.active : ""} onClick={() => {setActiveTab("withdrawals"); setCurrentPage(1);}}>Withdrawals {withdrawalRequests.filter(r => String(r.staff_id) === String(selectedStaff.id) && r.status === 'Pending').length > 0 && <span className={styles["tab-count"]}>{withdrawalRequests.filter(r => String(r.staff_id) === String(selectedStaff.id) && r.status === 'Pending').length}</span>}</button>
                    <button className={activeTab === "loans" ? styles.active : ""} onClick={() => {setActiveTab("loans"); setCurrentPage(1);}}>Loans</button>
                  </div>

                  <div className={styles["tab-container-content"]}>
                    {activeTab === "overview" && (
                      <div className={`${styles["tab-pane"]} ${styles["animate-fade"]}`}>
                        <div className={styles["profile-info-grid"]}>
                          <div className={styles["profile-info-card"]}>
                            <h3>Personal Information</h3>
                            <div className={styles["detail-list"]}>
                              <div><span>Full name</span><strong>{selectedStaff.name || 'Not set'}</strong></div>
                              <div><span>Role / Specialty</span><strong>{selectedStaff.role || 'Not set'}</strong></div>
                              <div><span>Phone</span><strong>{selectedStaff.phone || 'Not set'}</strong></div>
                              <div><span>Email</span><strong>{selectedStaff.email || 'Not set'}</strong></div>
                              <div><span>Date joined</span><strong>{joinedDate}</strong></div>
                              <div><span>Status</span><strong>{selectedStaff.status || 'Offline'}</strong></div>
                              <div className={styles["detail-wide"]}><span>Address</span><strong>{selectedStaff.address || 'Not set'}</strong></div>
                            </div>
                          </div>
                          <div className={styles["profile-summary-stack"]}>
                            <div className={styles["summary-tile"]}><Wallet size={19}/><div><span>Wallet Balance</span><strong>₦{Number(selectedStaff.wallet_balance || 0).toLocaleString()}</strong><small>Current payable balance</small></div></div>
                            <div className={styles["summary-tile"]}><History size={19}/><div><span>Total Services</span><strong>{stats.totalWork}</strong><small>All time</small></div></div>
                            <div className={styles["summary-tile"]}><Calendar size={19}/><div><span>Today's Services</span><strong>{stats.workToday}</strong><small>Completed today</small></div></div>
                          </div>
                        </div>
                        <div className={styles["overview-actions"]}>
                          <button className={styles["gold-action-btn"]} onClick={() => setActiveTab('task')}><PlusCircle size={16}/> Record Service</button>
                          <button className={styles["outline-action-btn"]} onClick={() => setActiveTab('withdrawals')}><CreditCard size={16}/> Withdrawal Requests</button>
                          <button className={styles["outline-action-btn"]} onClick={() => setShowLoanModal(true)}><Banknote size={16}/> Loan Management</button>
                        </div>
                      </div>
                    )}
                    {activeTab === "task" && (
                      <div className={`${styles["tab-pane"]} ${styles["animate-fade"]}`}>
                        <h3 className={styles["section-subtitle"]}><PlusCircle size={18} color="#caa45f" /> <span style={{color:"#fff"}}>Record New Task</span></h3>
                        <div className={styles["task-form"]}>
                          <input className={styles["task-field"]} placeholder="Task Title" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} />
                          <div className={styles["task-row"]}>
                            <div className={styles["amt-input-wrap"]}><span>₦</span><input type="number" placeholder="Price" value={taskAmount} onChange={(e) => setTaskAmount(e.target.value)} /></div>
                            <div className={styles["percent-input-wrap"]}>
                              <select value={taskCommission} onChange={(e) => setTaskCommission(e.target.value)}>
                                <option value="30">30% (Shop)</option>
                                <option value="35">35%</option>
                                <option value="40">40%</option>
                                <option value="45">45%</option>
                                <option value="50">50% (Personal)</option>
                                <option value="60">60%</option>
                              </select>
                            </div>
                            <button className={styles["btn-save-task"]} onClick={handleAddTask}>Record</button>
                          </div>
                          <div className={styles["calc-preview"]}>
                            <p className={styles["payout-hint"]}>Staff Earns: <strong>₦{liveStaffEarns.toLocaleString()}</strong></p>
                            <p className={styles["company-hint"]}>Company Share: <strong>₦{liveCompanyShare.toLocaleString()}</strong></p>
                          </div>
                        </div>

                        <div className={styles["bank-details-display"]}>
                          <h3 className={styles["section-subtitle-small"]}>
                            <CreditCard size={16} color="#caa45f" /> <span>Settlement Account</span>
                          </h3>
                          <div className={styles["bank-info-card"]}>
                            <div className={styles["bank-row"]}><label>BANK</label><p style={{color:'black'}}>{selectedStaff.bank_name || "Not Set"}</p></div>
                            <div className={styles["bank-row"]}><label>Account Number</label><p className={styles["ac-number"]}>{selectedStaff.account_number || "Not Set"}</p></div>
                            <div className={styles["bank-row"]}><label>Account Name</label><p style={{color:'black'}}>{selectedStaff.account_name || "Not Set"}</p></div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "work" && (
                      <div className={`${styles["tab-pane"]} ${styles["animate-fade"]}`}>
                        <h3 className={styles["section-subtitle"]}><History size={18} color="#caa45f" /> <span style={{color:'#fff'}}>Work History Log</span></h3>
                        <div className={styles["history-feed"]}>
                          {selectedStaff.history?.length > 0 ? (
                            <>
                              {paginate(selectedStaff.history).map(t => (
                                <div key={t.id} className={styles["history-item"]} style={{ cursor: 'pointer' }} onClick={() => { setActiveWorkItem(t); setShowWorkDetailsModal(true); }}>
                                  <div className={styles["h-info"]}>
                                    <strong>{t.title}</strong>
                                    <small>{new Date(t.date).toLocaleString()}</small>
                                  </div>
                                  <div style={{ display:'flex', alignItems:'center', gap: 15 }} onClick={(e) => e.stopPropagation()}>
                                    <span className={styles["h-earned"]}>+₦{Number(t.earned_amount).toLocaleString()}</span>
                                    <button className={styles["inline-delete-icon-btn"]} onClick={() => { setActiveWorkItem(t); handleDeleteTask(t.id); }}><Trash2 size={14} /></button>
                                  </div>
                                </div>
                              ))}
                              <Pagination totalItems={selectedStaff.history.length} itemsPerPage={itemsPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                            </>
                          ) : <div className={styles["empty-state"]}><Inbox size={40} /><p>No history found</p></div>}
                        </div>
                      </div>
                    )}

                    {activeTab === "transactions" && (
                      <div className={styles["tab-pane"]}>
                        <h3 className={styles["section-subtitle"]} style={{color: '#fff'}}>Payout History</h3>
                        <div className={styles["history-feed"]}>
                          {selectedStaff.transactions && selectedStaff.transactions.length > 0 ? (
                            <>
                              {paginate(selectedStaff.transactions).map((t) => (
                                <div key={t.id} className={styles["history-item"]}>
                                  <div className={styles["h-info"]}>
                                    <strong>{t.type}</strong>
                                    <small>{new Date(t.date).toLocaleString()}</small>
                                  </div>
                                  <span style={{color: t.type.includes('Loan') ? '#caa45f' : '#ff4d4d', fontWeight: 'bold'}}>
                                    {t.type.includes('Loan') ? '+' : '-'}₦{Number(t.amount).toLocaleString()}
                                  </span>
                                </div>
                              ))}
                              <Pagination totalItems={selectedStaff.transactions.length} itemsPerPage={itemsPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                            </>
                          ) : (
                            <div className={styles["empty-state"]}><Inbox size={40} /><p>No payout history found</p></div>
                          )}
                        </div>
                      </div>
                    )}

                    {activeTab === "withdrawals" && (
                      <div className={`${styles["tab-pane"]} ${styles["animate-fade"]}`}>
                        <div className={styles["withdrawal-section-head"]}>
                          <div><h3>Withdrawal Requests</h3><p>Review and manage payout requests for {selectedStaff.name}.</p></div>
                          <button className={styles["gold-action-btn"]} onClick={() => setShowWithdrawModal(true)}><PlusCircle size={15}/> {userRole === 'super-admin' ? 'New Deduction' : 'Request Payout'}</button>
                        </div>
                        <div className={styles["withdrawal-table"]}>
                          <div className={styles["withdrawal-table-head"]}><span>Amount</span><span>Status</span><span>Request date</span><span>Action</span></div>
                          {withdrawalRequests.filter(r => String(r.staff_id) === String(selectedStaff.id)).length > 0 ? withdrawalRequests.filter(r => String(r.staff_id) === String(selectedStaff.id)).map(req => (
                            <div className={styles["withdrawal-table-row"]} key={req.id}>
                              <strong>₦{Number(req.amount || 0).toLocaleString()}</strong>
                              <span className={`${styles["request-status"]} ${styles[(req.status || 'pending').toLowerCase()]}`}>{req.status}</span>
                              <span>{req.created_at ? new Date(req.created_at).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'}) : '—'}</span>
                              <div className={styles["request-actions"]}>{userRole === 'super-admin' && req.status === 'Pending' ? <><button onClick={() => handleApproveRequest(req)}>Approve</button><button className={styles["reject-request"]} onClick={() => handleDeclineRequest(req)}>Reject</button></> : <span>—</span>}</div>
                            </div>
                          )) : <div className={styles["withdrawal-empty"]}><Inbox size={28}/><p>No withdrawal requests for this staff member.</p></div>}
                        </div>
                      </div>
                    )}

                    {activeTab === "loans" && (
                      <div className={`${styles["tab-pane"]} ${styles["animate-fade"]}`}>
                        <h3 className={styles["section-subtitle"]}><Banknote size={18} color="#caa45f" /> <span style={{color:'#fff'}}>Active Loan Allocations</span></h3>
                        <div className={styles["history-feed"]}>
                          {staffLoans.length > 0 ? (
                            <>
                              {paginate(staffLoans).map(loan => (
                                <div key={loan.id} className={styles["history-item"]} style={{ borderLeftColor: loan.status === 'Active' ? '#00ff9c' : '#caa45f' }}>
                                  <div className={styles["h-info"]}>
                                    <strong>₦{Number(loan.principal_amount).toLocaleString()} ({loan.deduction_frequency})</strong>
                                    <small>Remaining Balance: ₦{Number(loan.remaining_balance).toLocaleString()} • Deducting: ₦{loan.deduction_amount}</small>
                                  </div>
                                  <div style={{ display:'flex', gap: 8, alignItems:'center' }}>
                                    <span className={styles["status-pill"]} style={{ color: loan.status === 'Active' ? '#00ff9c' : '#caa45f', paddingRight:10 }}>{loan.status}</span>
                                    {userRole === 'super-admin' && loan.status === 'Pending' && (
                                      <>
                                        <button className={styles["approve-btn"]} onClick={() => handleProcessLoanAdmin(loan.id, 'Approved')}>Approve</button>
                                        <button className={styles["decline-btn"]} onClick={() => handleProcessLoanAdmin(loan.id, 'Declined')}>Decline</button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                              <Pagination totalItems={staffLoans.length} itemsPerPage={itemsPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                            </>
                          ) : <div className={styles["empty-state"]}><Inbox size={40} /><p>No loans recorded for this staff member.</p></div>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* DETAILED WORK HISTORY BREAKDOWN OVERLAY CARD */}
      {showWorkDetailsModal && activeWorkItem && (
        <div className={styles["modal-fixed-overlay"]} style={{ zIndex: 10005 }}>
          <div className={styles["premium-detail-card"]}>
            <div className={styles["terminal-header"]}>
              <div className={styles["terminal-title-wrap"]}>
                <span className={styles["live-indicator-dot"]}></span>
                <h3>SERVICE TRANSACTION DETAILS</h3>
              </div>
              <button className={styles["close-circle-btn"]} onClick={() => setShowWorkDetailsModal(false)}><X size={18} /></button>
            </div>
            <div className={styles["receipt-body"]}>
              <div className={styles["receipt-row-main"]}>
                <span className={styles["receipt-label"]}>SERVICE RENDERED</span>
                <h2 className={styles["receipt-value-bold"]}>{activeWorkItem.title.toUpperCase()}</h2>
              </div>
              <div className={styles["architectural-divider"]}></div>
              <div className={styles["total-paid-block"]}>
                <span className={styles["receipt-label"]}>TOTAL AMOUNT PAID BY CUSTOMER</span>
                <h1 className={styles["total-amount-display"]}>₦{Number(Number(activeWorkItem.earned_amount) + Number(activeWorkItem.company_share || 0)).toLocaleString()}</h1>
              </div>
              <div className={styles["architectural-divider"]}></div>
              <div className={styles["receipt-grid"]}>
                <div className={styles["receipt-tile"]}><span className={styles["tile-label"]}>STAFF COMMISSION</span><span className={styles["tile-price-green"]}>₦{Number(activeWorkItem.earned_amount).toLocaleString()}</span></div>
                <div className={styles["receipt-tile"]}><span className={styles["tile-label"]}>COMPANY SHARE</span><span className={styles["tile-price-gold"]}>₦{Number(activeWorkItem.company_share || 0).toLocaleString()}</span></div>
              </div>
              <div className={styles["architectural-divider"]}></div>
              <button className={styles["terminal-revert-btn"]} onClick={() => handleDeleteTask(activeWorkItem.id)} disabled={isActionLoading}><Trash2 size={14} /> {isActionLoading ? "ADJUSTING METRICS..." : "REMOVE TRANSACTION"}</button>
              <div className={styles["receipt-timestamp-footer"]}><Calendar size={12} color="#caa45f" /><span>RECORDED: {new Date(activeWorkItem.date).toUTCString()}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* LOAN ADVANCE MODAL APPLICATION FORM */}
      {showLoanModal && (
        <div className={styles["modal-fixed-overlay"]} style={{ zIndex: 10002 }}>
          <div className={styles["premium-loan-card"]}>
            <div className={styles["terminal-header"]}>
              <div className={styles["terminal-title-wrap"]}><Banknote size={18} color="#caa45f" /><h3>STAFF LOAN / ADVANCE</h3></div>
              <button className={styles["close-circle-btn"]} onClick={() => setShowLoanModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleApplyLoan} className={styles["premium-terminal-form"]}>
              <div className={styles["form-description-block"]}><Info size={14} color="#caa45f" /><p>This entry authorizes a balance advance. Repayment logs handle outstanding balances automatically via task completions.</p></div>
              <div className={styles["input-field-container"]}>
                <label className={styles["premium-input-label"]}>LOAN AMOUNT (₦)</label>
                <div className={styles["premium-input-wrapper"]}><span className={styles["input-currency-prefix"]}>₦</span><input type="number" placeholder="0.00" className={styles["premium-terminal-input"]} value={loanAmount} onChange={e => setLoanAmount(e.target.value)} /></div>
              </div>
              <div className={styles["input-field-container"]}>
                <label className={styles["premium-input-label"]}>DEDUCTION AMOUNT (₦)</label>
                <div className={styles["premium-input-wrapper"]}><span className={styles["input-currency-prefix"]}>₦</span><input type="number" placeholder="0.00" className={styles["premium-terminal-input"]} value={loanDeduction} onChange={e => setLoanDeduction(e.target.value)} /></div>
              </div>
              <div className={styles["input-field-container"]}>
                <label className={styles["premium-input-label"]}>REPAYMENT FREQUENCY</label>
                <select value={loanFrequency} onChange={e => setLoanFrequency(e.target.value)} className={styles["premium-terminal-select"]}>
                  <option value="daily">Deduct daily from staff earnings</option>
                  <option value="weekly">Deduct weekly from staff earnings</option>
                </select>
              </div>
              <button className={styles["premium-authorize-btn"]} type="submit" disabled={isActionLoading}>{isActionLoading ? "SUBMITTING..." : "SUBMIT LOAN REQUEST"}</button>
            </form>
          </div>
        </div>
      )}

      {/* WITHDRAWAL & LOAN REPAYMENT MANUAL TERMINAL MODAL */}
      {showWithdrawModal && (
        <div className={styles["modal-fixed-overlay"]} style={{ zIndex: 10001 }}>
          <div className={`${styles["withdrawal-mini-card"]} ${styles["withdrawal-modal-redesign"]}`}>
            <div className={styles["modal-nav"]}>
              <div><span className={styles["modal-kicker"]}>STAFF PAYOUT</span><h3 className={styles["withdraw-modal-title"]}>Record Withdrawal</h3><p className={styles["withdraw-modal-copy"]}>Deduct a payout or loan repayment from this staff member’s available balance.</p></div>
              <X className={styles["close-x-btn"]} onClick={() => setShowWithdrawModal(false)} />
            </div>

            <div className={styles["withdrawal-body-box"]}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '25px', background: '#090909', padding: '12px', border: '1px solid #111' }}>
                <div>
                  <span style={{ fontSize: '9px', color: '#777', fontWeight: '800', display: 'block' }}>AVAILABLE WALLET</span>
                  <strong className={styles["withdraw-wallet-value"]}>₦{Number(selectedStaff.wallet_balance).toLocaleString()}</strong>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '9px', color: '#777', fontWeight: '800', display: 'block' }}>DEBT LEDGER</span>
                  <strong className={styles["withdraw-debt-value"]}>₦{Number(selectedStaff.loan_balance || 0).toLocaleString()}</strong>
                </div>
              </div>

              <div className={styles["input-field-container"]} style={{ marginBottom: '20px' }}>
                <label className={styles["premium-input-label"]}>AMOUNT (₦)</label>
                <div className={`${styles["amt-input-wrap"]} ${styles["large-withdraw-input"]}`} style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '15px', top: '15px', color: '#caa45f', fontWeight: '900', fontSize: '20px' }}>₦</span>
                  <input 
                    type="number" 
                    placeholder="0.00" 
                    value={withdrawAmount} 
                    onChange={(e) => setWithdrawAmount(e.target.value)} 
                    style={{ paddingLeft: '40px', fontSize: '22px', fontWeight: '900', background: '#090909', border: '1px solid #111', width: '100%', color: '#fff' }}
                  />
                </div>
              </div>

              {userRole === "super-admin" && (
                <div className={styles["input-field-container"]} style={{ marginBottom: '25px' }}>
                  <label className={styles["premium-input-label"]}>TRANSACTION TYPE</label>
                  <select 
                    id="deductionContextSelector"
                    className={styles["premium-terminal-select"]}
                    style={{ width: '100%', padding: '16px', background: '#090909', border: '1px solid #111', color: '#caa45f', fontWeight: '900', fontSize: '11px', letterSpacing: '1px' }}
                  >
                    <option value="payout">Staff withdrawal / payout</option>
                    <option value="loan_paydown">Loan repayment</option>
                  </select>
                </div>
              )}

               { notify && (
                  <div style={{ marginBottom: '20px', padding: '12px', background: notify.includes('successfully') ? 'rgba(0, 255, 156, 0.1)' : 'rgba(255, 77, 77, 0.1)', color: notify.includes('successfully') ? '#00ff9c' : '#ff4d4d', border: `1px solid ${notify.includes('successfully') ? '#00ff9c' : '#ff4d4d'}`, fontWeight: '900', fontSize: '11px', letterSpacing: '1px' }}>
                    {notify}
                  </div>
                )}

              <button 
                className={styles["confirm-payout-btn"]} 
                onClick={async () => {
                  const context = document.getElementById("deductionContextSelector")?.value || "payout";
                  const amt = Number(withdrawAmount);
                  if (!amt || amt <= 0) return toast.error("Invalid transaction amount bounds.");

                  setIsActionLoading(true);
                  const loadToast = toast.loading("Balancing ledgers parameters...");

                  try {
                    if (context === "loan_paydown") {
                      await axios.post("/api/admin/loans/manual-repay", { staffId: selectedStaff.id, amount: amt });
                      const updated = {
                        ...selectedStaff,
                        wallet_balance: Number(selectedStaff.wallet_balance) - amt,
                        loan_balance: Math.max(0, Number(selectedStaff.loan_balance) - amt)
                      };
                      setSelectedStaff(updated);
                      setStaffList(prev => prev.map(s => s.id === updated.id ? updated : s));
                      setNotify('Loan balance manually adjusted successfully!');
                    } else {
                      await handleWithdrawInitiation();
                      setNotify(''); 
                    }
                    setShowWithdrawModal(false);
                    setWithdrawAmount("");
                  } catch (err) {
                    setNotify('Transaction distribution failure.');
                  } finally {
                    setIsActionLoading(false);
                  }
                }}
                disabled={isActionLoading}
                style={{ width: '100%', background: '#caa45f', color: '#000', padding: '16px', fontWeight: '900', border: 'none', cursor: 'pointer', letterSpacing: '1px' }}
              >
                {isActionLoading ? "PROCESSING..." : "CONFIRM TRANSACTION"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD STAFF MULTI-STEP MODAL */}
      {showAddModal && (
        <div className={styles["modal-fixed-overlay"]}>
          <div className={styles["add-staff-modal-container"]}>
            <div className={`${styles["modal-nav"]} ${styles["add-modal-head"]}`}>
               <div>
                 <span className={styles["modal-kicker"]}>TEAM MANAGEMENT</span>
                 <h2 className={styles["add-modal-title"]}>Add Staff Member</h2>
                 <p className={styles["add-modal-copy"]}>Create a new team profile in three simple steps.</p>
               </div>
               <button className={styles["close-x-btn"]} onClick={() => setShowAddModal(false)}><X size={20} /></button>
            </div>
            <div className={styles["step-pills"]}>
              <div className={`${styles["p-pill"]} ${step === 1 ? styles.active : styles.done}`}><b>1</b><span>Personal Details</span></div>
              <div className={`${styles["p-pill"]} ${step === 2 ? styles.active : (step > 2 ? styles.done : "")}`}><b>2</b><span>Bank Details</span></div>
              <div className={`${styles["p-pill"]} ${step === 3 ? styles.active : ""}`}><b>3</b><span>Guarantor</span></div>
            </div>
            <div className={styles["modal-scroll-content"]}>
               {step === 1 && (
                  <div className={styles["input-group-grid"]}>
                     <div className={styles["i-box"]}><label>Full Name</label><input type="text" placeholder="Enter full name" value={newStaff.name} onChange={e => setNewStaff({...newStaff, name: e.target.value})} />{errors.name && <span className={styles.errorText}>{errors.name}</span>}</div>
                     <div className={styles["i-box"]}><label>Role</label>
                        <select value={newStaff.role} onChange={e => setNewStaff({...newStaff, role: e.target.value})}>
                           <option value="Barber">Barber</option>
                           <option value="Stylist">Stylist</option>
                           <option value="Pedicurist">Pedicurist</option>
                           <option value="Nail Technician">Nail Technician</option>
                           <option value="Lash Tech & Microblading">Lash Tech & Microblading</option>
                           <option value="Astheticians & Facial">Astheticians & Facial</option>
                        </select>
                     </div>
                     <div className={styles["i-box"]}><label>Email Address</label><input type="email" placeholder="Enter email address" value={newStaff.email} onChange={e => setNewStaff({...newStaff, email: e.target.value})} />{errors.email && <span className={styles.errorText}>{errors.email}</span>}</div>
                     <div className={styles["i-box"]}><label>Phone Number</label><input type="text" placeholder="0812 345 6789" value={newStaff.phone} onChange={e => setNewStaff({...newStaff, phone: e.target.value})} />{errors.phone && <span className={styles.errorText}>{errors.phone}</span>}</div>
                     <div className={styles["i-box"]}><label>Date of Birth</label><input type="date" value={newStaff.birthday} onChange={e => setNewStaff({...newStaff, birthday: e.target.value})} />{errors.birthday && <span className={styles.errorText}>{errors.birthday}</span>}</div>
                     <div className={`${styles["i-box"]} ${styles["full-width"]}`}><label>Residential Address</label><input type="text" placeholder="Enter residential address" value={newStaff.address} onChange={e => setNewStaff({...newStaff, address: e.target.value})} />{errors.address && <span className={styles.errorText}>{errors.address}</span>}</div>
                  </div>
               )}
               {step === 2 && (
                  <div className={styles["input-group-grid"]}>
                     <div className={`${styles["i-box"]} ${styles["full-width"]}`}><label>Bank Name</label><input type="text" placeholder="e.g. Zenith Bank" value={newStaff.bank_name} onChange={e => setNewStaff({...newStaff, bank_name: e.target.value})} />{errors.bank_name && <span className={styles.errorText}>{errors.bank_name}</span>}</div>
                     <div className={styles["i-box"]}><label>Account Number</label><input type="text" maxLength="10" placeholder="0123456789" value={newStaff.account_number} onChange={e => setNewStaff({...newStaff, account_number: e.target.value})} />{errors.account_number && <span className={styles.errorText}>{errors.account_number}</span>}</div>
                     <div className={styles["i-box"]}><label>Account Name</label><input type="text" placeholder="Name on Account" value={newStaff.account_name} onChange={e => setNewStaff({...newStaff, account_name: e.target.value})} />{errors.account_name && <span className={styles.errorText}>{errors.account_name}</span>}</div>
                  </div>
               )}
               {step === 3 && (
                  <div className={styles["input-group-grid"]}>
                     <div className={styles["i-box"]}><label>Guarantor Name</label><input type="text" value={newStaff.guarantor_name} onChange={e => setNewStaff({...newStaff, guarantor_name: e.target.value})} /></div>
                     <div className={styles["i-box"]}><label>Guarantor Phone</label><input type="text" value={newStaff.guarantor_phone} onChange={e => setNewStaff({...newStaff, guarantor_phone: e.target.value})} /></div>
                     <div className={`${styles["i-box"]} ${styles["full-width"]}`}><label>Guarantor Address</label><input type="text" value={newStaff.guarantor_address} onChange={e => setNewStaff({...newStaff, guarantor_address: e.target.value})} /></div>
                  </div>
               )}
            </div>
            <div className={styles["modal-footer-flex"]}>
               {step > 1 && <button className={styles["back-link-btn"]} onClick={() => setStep(step - 1)}>Back</button>}
               <button className={styles["next-gold-btn"]} onClick={async () => {
                  if (step === 1 && validateStep1()) setStep(2);
                  else if (step === 2 && validateStep2()) setStep(3);
                  else if (step === 3) {
                    try {
                      await axios.post(`/api/add-staff`, newStaff);
                      toast.success("Staff Registered Successfully!");
                      setShowAddModal(false);
                      window.location.reload();
                    } catch(err) { 
                      toast.error("Registration failed.");
                    }
                  }
               }}>
                {step === 3 ? "Save Staff" : "Next"} <ChevronRight size={18} />
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pagination({ totalItems, itemsPerPage, currentPage, setCurrentPage }) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  if (totalPages <= 1) return null;
  return (
    <div className={styles["pagination-controls"]}>
      <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}><ChevronLeft size={16} /></button>
      <span>Page {currentPage} of {totalPages}</span>
      <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}><ChevronRight size={16} /></button>
    </div>
  );
}